import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AuthService,
  IdentityService,
  SessionStorage,
  TeamService,
  filterRolesByClinicProducts,
  useAuth,
  useNotifications,
  withClinicCounts,
} from './deps.js';

/**
 * @typedef {import('../fetcher/dto/UserData.js').UserData} UserData
 * @typedef {import('../fetcher/dto/ClinicData.js').ClinicData} ClinicData
 * @typedef {import('../fetcher/dto/TeamData.js').TeamData} TeamData
 * @typedef {import('../fetcher/dto/UserRoleData.js').UserRoleData} UserRoleData
 */

const TABLE_IDS = {
  clinic: {
    users: 'AdminUsersTable',
    teams: 'AdminTeamsTable',
    clinics: 'AdminClinicsTable',
    roles: 'AdminRolesTable',
  },
  master: {
    users: 'MasterAdminUsersTable',
    teams: 'MasterAdminTeamsTable',
    clinics: 'MasterAdminClinicsTable',
    roles: 'MasterAdminRolesTable',
  },
};

const useAdminDirectory = ({ variant, fetchClinics, fetchClinicUsers, fetchTeams }) => {
  const isMaster = variant === 'master';
  /** @type {[Array<ClinicData & { userCount?: number, teamCount?: number }>, Function]} */
  const [clinics, setClinics] = useState([]);
  const [adminSelectedClinicId, setAdminSelectedClinicId] = useState(null);
  /** @type {[UserData[], Function]} */
  const [users, setUsers] = useState([]);
  /** @type {[Object<string, UserRoleData>, Function]} */
  const [allRoles, setAllRoles] = useState({});
  /** @type {[TeamData[], Function]} */
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState({
    clinic: true,
    users: !isMaster,
    roles: true,
    teams: !isMaster,
  });

  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const identityService = useMemo(() => new IdentityService(), []);
  const authService = useMemo(() => new AuthService(), []);
  const teamService = useMemo(() => new TeamService(), []);
  const usersRequestId = useRef(0);
  const teamsRequestId = useRef(0);

  const selectedClinicProducts = clinics.find((c) => c.clinicId === adminSelectedClinicId)?.products
    ?? (isMaster ? undefined : user?.clinicProducts?.[adminSelectedClinicId]);
  const roles = useMemo(
    () => filterRolesByClinicProducts(allRoles, selectedClinicProducts),
    [allRoles, selectedClinicProducts]
  );

  const clinicHeaderOverride = useMemo(
    () => (adminSelectedClinicId
      ? { headers: { 'X-Target-Clinic-Id': adminSelectedClinicId } }
      : {}),
    [adminSelectedClinicId]
  );

  const reloadClinics = useCallback(async () => {
    setLoading((prev) => ({ ...prev, clinic: true }));
    try {
      const data = await fetchClinics();
      let clinicList = data || [];

      if (!isMaster) {
        clinicList = await Promise.all(clinicList.map(async (clinic) => {
          const [usersData, teamsData] = await Promise.all([
            fetchClinicUsers(clinic.clinicId).catch(() => []),
            fetchTeams(clinic.clinicId).catch(() => []),
          ]);
          return withClinicCounts(clinic, usersData.length, teamsData.length);
        }));
      }

      setClinics(clinicList);

      if (clinicList.length > 0) {
        const defaultId = isMaster
          ? clinicList[0].clinicId
          : (SessionStorage.getSelectedClinicId() || clinicList[0].clinicId);
        setAdminSelectedClinicId((current) => current || defaultId);
      }

      setLoading((prev) => ({ ...prev, clinic: false }));
    } catch (error) {
      console.error('Failed to fetch clinics:', error);
      addNotification('fetch-clinic-error', 'Failed to fetch clinics', { isError: true });
      setLoading((prev) => ({ ...prev, clinic: false }));
    }
  }, [addNotification, fetchClinicUsers, fetchClinics, fetchTeams, isMaster]);

  const reloadRoles = useCallback(async () => {
    setLoading((prev) => ({ ...prev, roles: true }));
    try {
      const data = await identityService.fetchRoles();
      setAllRoles(data);
      setLoading((prev) => ({ ...prev, roles: false }));
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      addNotification('fetch-roles-error', 'Failed to fetch user roles', { isError: true });
      setLoading((prev) => ({ ...prev, roles: false }));
    }
  }, [addNotification, identityService]);

  const reloadUsers = useCallback(async () => {
    const requestId = ++usersRequestId.current;
    setLoading((prev) => ({ ...prev, users: true }));
    try {
      const data = await fetchClinicUsers(adminSelectedClinicId);
      if (requestId !== usersRequestId.current) {
        return;
      }
      setUsers(data);
      setLoading((prev) => ({ ...prev, users: false }));
    } catch (error) {
      if (requestId !== usersRequestId.current) {
        return;
      }
      console.error('Failed to fetch users:', error);
      addNotification('fetch-users-error', 'Failed to fetch clinic users', { isError: true });
      setLoading((prev) => ({ ...prev, users: false }));
    }
  }, [addNotification, adminSelectedClinicId, fetchClinicUsers]);

  const reloadTeams = useCallback(async () => {
    const requestId = ++teamsRequestId.current;
    setLoading((prev) => ({ ...prev, teams: true }));
    try {
      const data = await fetchTeams(adminSelectedClinicId);
      if (requestId !== teamsRequestId.current) {
        return;
      }
      setTeams(data);
      setLoading((prev) => ({ ...prev, teams: false }));
    } catch (error) {
      if (requestId !== teamsRequestId.current) {
        return;
      }
      console.error('Failed to fetch teams:', error);
      addNotification('fetch-teams-error', 'Failed to fetch teams', { isError: true });
      setLoading((prev) => ({ ...prev, teams: false }));
    }
  }, [addNotification, adminSelectedClinicId, fetchTeams]);

  useEffect(() => {
    reloadClinics();
    reloadRoles();
    // Fetch once on mount. Fetchers are provided by the hosting app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!adminSelectedClinicId) {
      setUsers([]);
      setTeams([]);
      return;
    }
    reloadUsers();
    reloadTeams();
  }, [adminSelectedClinicId, reloadUsers, reloadTeams]);

  return {
    variant,
    isMaster,
    tableIds: TABLE_IDS[variant] || TABLE_IDS.clinic,
    clinics,
    adminSelectedClinicId,
    setAdminSelectedClinicId,
    users,
    setUsers,
    teams,
    setTeams,
    roles,
    loading,
    clinicHeaderOverride,
    identityService,
    authService,
    teamService,
    reloadUsers,
    reloadTeams,
    reloadClinics,
    addNotification,
  };
};

export default useAdminDirectory;
